/**
 * Les MARQUEURS d'un texte à champs — de part et d'autre de la pastille.
 *
 * Le texte enregistré est celui que le serveur sait remplacer :
 * `{{ clé }}` (`FormNotifier::interpolate`). L'éditeur, lui, affiche le champ
 * comme une pastille intitulée. Ces deux fonctions font la traduction, dans
 * les deux sens, et sont volontairement PURES : c'est ce qui permet de vérifier
 * l'aller-retour — ouvrir puis enregistrer sans rien changer ne doit jamais
 * réécrire un message.
 *
 * Elles vivent dans un module à part de `TokenTextField.jsx` : un fichier de
 * composant qui exporte autre chose qu'un composant casse le rechargement à
 * chaud de Vite (règle `react-refresh/only-export-components`).
 */

/** Le marqueur reconnu, ici comme côté serveur. */
const TOKEN_RE = /\{\{\s*([A-Za-z0-9 _.-]+?)\s*\}\}/g

/** `Prénom`, `first_name`, `firstName` : un seul et même champ (cf. FormNotifier). */
const normalizeKey = (key) => String(key ?? '').replace(/[^A-Za-z0-9]/g, '').toLowerCase()

/** Sans casse ni accents : ce qui rend « prenom » trouvable en tapant « @pré ». */
const foldText = (text) => String(text ?? '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()

/** Les champs qui répondent à ce qui est tapé après « @ ». */
export const filterTokens = (tokens, query) => {
  const q = foldText(query).trim()
  if (q === '') return tokens
  return tokens.filter(t => foldText(t.label).includes(q) || foldText(t.key).includes(q))
}

/**
 * Le texte à marqueurs → un document TipTap.
 *
 * Un marqueur dont la clé ne correspond à aucun champ connu reste du TEXTE :
 * il vient d'un formulaire qui a perdu ce champ, ou d'une saisie à la main, et
 * l'effacer en silence perdrait le travail de quelqu'un.
 */
export const toDocument = (value, tokens) => {
  const byKey = new Map((tokens ?? []).map(t => [normalizeKey(t.key), t]))
  const lines = String(value ?? '').split('\n')

  return {
    type: 'doc',
    content: lines.map((line) => {
      const nodes = []
      let cursor = 0
      TOKEN_RE.lastIndex = 0
      let match
      while ((match = TOKEN_RE.exec(line)) !== null) {
        const token = byKey.get(normalizeKey(match[1]))
        if (!token) continue
        if (match.index > cursor) nodes.push({ type: 'text', text: line.slice(cursor, match.index) })
        nodes.push({ type: 'mention', attrs: { id: token.key, label: token.label } })
        cursor = match.index + match[0].length
      }
      if (cursor < line.length) nodes.push({ type: 'text', text: line.slice(cursor) })
      return nodes.length > 0 ? { type: 'paragraph', content: nodes } : { type: 'paragraph' }
    }),
  }
}

/**
 * Le document → le texte à marqueurs.
 *
 * Sérialisé depuis le JSON de l'éditeur plutôt que par `getText()` : la valeur
 * enregistrée ne dépend alors d'aucun réglage de rendu de TipTap.
 */
export const fromDocument = (doc) => (doc?.content ?? [])
  .map(block => (block.content ?? [])
    .map(node => {
      if (node.type === 'mention') return `{{ ${node.attrs?.id ?? ''} }}`
      if (node.type === 'hardBreak') return '\n'
      return node.text ?? ''
    })
    .join(''))
  .join('\n')
